import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  User,
  Building2,
  Phone,
  Mail,
  MapPin,
  Globe,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  Save,
  Upload,
  FileText,
  Shield,
  Calendar,
  Trash2,
  Plus,
  X,
  CheckCircle2,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ContractorSettingsModal
 * Settings/Profile management for contractors
 * 
 * Features:
 * - Update profile info (name, phone, address, etc.)
 * - Upload credentials (licenses, certifications, insurance)
 * - Change password
 * - View account info
 */
export default function ContractorSettingsModal({ open, onClose, token }) {
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Profile state
  const [profile, setProfile] = useState({
    name: "",
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    website: "",
    categories: [],
    notes: ""
  });
  
  // Credentials state
  const [credentials, setCredentials] = useState([]);
  const [showAddCredential, setShowAddCredential] = useState(false);
  const [newCredential, setNewCredential] = useState({
    credential_type: "license",
    credential_name: "",
    expiry_date: "",
    notes: ""
  });
  
  // Password state
  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: ""
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (open && token) {
      fetchProfile();
    }
  }, [open, token]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/contractor/profile/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to fetch profile");
      const data = await res.json();
      setProfile(data.profile || {});
      setCredentials(data.profile?.credentials || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/contractor/profile/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: profile.name,
          company_name: profile.company_name,
          contact_name: profile.contact_name,
          phone: profile.phone,
          address: profile.address,
          city: profile.city,
          state: profile.state,
          zip_code: profile.zip_code,
          website: profile.website,
          notes: profile.notes
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Failed to save");
      }
      
      toast.success("Profile updated successfully");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddCredential = async () => {
    if (!newCredential.credential_name) {
      toast.error("Credential name is required");
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/contractor/profile/credentials`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newCredential)
      });
      
      if (!res.ok) throw new Error("Failed to add credential");
      
      const data = await res.json();
      setCredentials([...credentials, data.credential]);
      setShowAddCredential(false);
      setNewCredential({ credential_type: "license", credential_name: "", expiry_date: "", notes: "" });
      toast.success("Credential added");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCredential = async (credentialId) => {
    if (!confirm("Delete this credential?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/contractor/profile/credentials/${credentialId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error("Failed to delete");
      
      setCredentials(credentials.filter(c => c.id !== credentialId));
      toast.success("Credential deleted");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new !== passwords.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (passwords.new.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      const res = await fetch(`${API_URL}/api/contractor/profile/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwords.current,
          new_password: passwords.new
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Failed to change password");
      }
      
      toast.success("Password changed successfully");
      setPasswords({ current: "", new: "", confirm: "" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setChangingPassword(false);
    }
  };

  const getCredentialTypeLabel = (type) => {
    const types = {
      license: "License",
      certification: "Certification",
      insurance: "Insurance",
      coi: "Certificate of Insurance",
      bond: "Bond",
      other: "Other"
    };
    return types[type] || type;
  };

  const getCredentialStatusColor = (status) => {
    switch (status) {
      case "approved": return "text-green-600 bg-green-100";
      case "pending_review": return "text-amber-600 bg-amber-100";
      case "expired": return "text-red-600 bg-red-100";
      default: return "text-slate-600 bg-slate-100";
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-emerald-600" />
            Settings & Profile
          </DialogTitle>
          <DialogDescription>
            Manage your account information, credentials, and security settings
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Profile</span>
            </TabsTrigger>
            <TabsTrigger value="credentials" className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Credentials</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span className="hidden sm:inline">Security</span>
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Company Name</Label>
                <div className="relative mt-1">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={profile.company_name || ""}
                    onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                    className="pl-10"
                    placeholder="Your Company LLC"
                  />
                </div>
              </div>
              <div>
                <Label>Contact Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={profile.contact_name || ""}
                    onChange={(e) => setProfile({ ...profile, contact_name: e.target.value })}
                    className="pl-10"
                    placeholder="John Doe"
                  />
                </div>
              </div>
              <div>
                <Label>Email (Read-only)</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={profile.email || ""}
                    className="pl-10 bg-slate-50"
                    disabled
                  />
                </div>
              </div>
              <div>
                <Label>Phone</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={profile.phone || ""}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    className="pl-10"
                    placeholder="555-123-4567"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Address</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={profile.address || ""}
                    onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                    className="pl-10"
                    placeholder="123 Main St"
                  />
                </div>
              </div>
              <div>
                <Label>City</Label>
                <Input
                  value={profile.city || ""}
                  onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                  placeholder="Houston"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>State</Label>
                  <Input
                    value={profile.state || ""}
                    onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                    placeholder="TX"
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label>ZIP</Label>
                  <Input
                    value={profile.zip_code || ""}
                    onChange={(e) => setProfile({ ...profile, zip_code: e.target.value })}
                    placeholder="77001"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Website</Label>
                <div className="relative mt-1">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={profile.website || ""}
                    onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                    className="pl-10"
                    placeholder="https://yourcompany.com"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={profile.notes || ""}
                  onChange={(e) => setProfile({ ...profile, notes: e.target.value })}
                  placeholder="Additional information..."
                  rows={3}
                />
              </div>
            </div>
            
            <Button onClick={handleSaveProfile} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Profile
            </Button>
          </TabsContent>

          {/* Credentials Tab */}
          <TabsContent value="credentials" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-600">Manage your licenses, certifications, and insurance documents</p>
              <Button size="sm" onClick={() => setShowAddCredential(true)}>
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            
            {credentials.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                <Shield className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600">No credentials uploaded yet</p>
                <p className="text-sm text-slate-500">Add your licenses, certifications, and insurance</p>
              </div>
            ) : (
              <div className="space-y-3">
                {credentials.map((cred) => (
                  <div key={cred.id} className="flex items-center justify-between p-4 bg-white border rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{cred.name}</p>
                        <p className="text-sm text-slate-500">{getCredentialTypeLabel(cred.type)}</p>
                        {cred.expiry_date && (
                          <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            Expires: {cred.expiry_date}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${getCredentialStatusColor(cred.status)}`}>
                        {cred.status === "pending_review" ? "Pending" : cred.status}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCredential(cred.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Credential Modal */}
            <Dialog open={showAddCredential} onOpenChange={setShowAddCredential}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Credential</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Type</Label>
                    <select
                      value={newCredential.credential_type}
                      onChange={(e) => setNewCredential({ ...newCredential, credential_type: e.target.value })}
                      className="w-full mt-1 p-2 border rounded-md"
                    >
                      <option value="license">License</option>
                      <option value="certification">Certification</option>
                      <option value="insurance">Insurance</option>
                      <option value="coi">Certificate of Insurance</option>
                      <option value="bond">Bond</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label>Name / Description</Label>
                    <Input
                      value={newCredential.credential_name}
                      onChange={(e) => setNewCredential({ ...newCredential, credential_name: e.target.value })}
                      placeholder="e.g., HVAC Contractor License #12345"
                    />
                  </div>
                  <div>
                    <Label>Expiry Date (Optional)</Label>
                    <Input
                      type="date"
                      value={newCredential.expiry_date}
                      onChange={(e) => setNewCredential({ ...newCredential, expiry_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={newCredential.notes}
                      onChange={(e) => setNewCredential({ ...newCredential, notes: e.target.value })}
                      placeholder="Additional details..."
                      rows={2}
                    />
                  </div>
                  <Button onClick={handleAddCredential} disabled={saving} className="w-full">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Add Credential
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-4 mt-4">
            <div className="bg-slate-50 rounded-lg p-4 border">
              <h3 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Change Password
              </h3>
              <div className="space-y-4">
                <div>
                  <Label>Current Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type={showPasswords ? "text" : "password"}
                      value={passwords.current}
                      onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                      className="pl-10 pr-10"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(!showPasswords)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    >
                      {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type={showPasswords ? "text" : "password"}
                      value={passwords.new}
                      onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                      className="pl-10"
                      placeholder="Minimum 8 characters"
                    />
                  </div>
                </div>
                <div>
                  <Label>Confirm New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type={showPasswords ? "text" : "password"}
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                      className="pl-10"
                      placeholder="Re-enter new password"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleChangePassword}
                  disabled={changingPassword || !passwords.current || !passwords.new || !passwords.confirm}
                  className="w-full"
                >
                  {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                  Change Password
                </Button>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-800">Account Security</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Use a strong password with at least 8 characters. Never share your password with anyone.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
